import {
  Channel,
  ChannelInfo,
  CustomChannelPlatform,
  FacebookChannelPlatform,
  ChannelDistribution,
  VideoCodec,
  VideoFormatProfile,
  VideoFormatProgressive,
  AudioCodec,
  AudioChannel,
  DefaultPushCodecSettings,
  WorkingHoursSettings,
  UploadPlaylistCsvSettings,
  ChannelSignalCommandSetting,
  ChannelFormat,
  ChannelPushSrtFormat,
  ChannelPushMpgtsSettings,
  ChannelPushSettings,
  ChannelPushSrtSettings,
  ChannelPushFlvSettings,
  AudioLangSettings,
  ClosedCaptionSetting,
} from "../../../domain/channel";
import { ChannelPlatformType, StreamingProtocol } from "../../../domain/enums";
import { Injectable } from "@angular/core";
import moment from "moment";
import {
  AudioCodecSettings,
  ChannelCodec,
  Resolution,
  ResolutionPreset,
  VideoCodecSettings,
} from "../../../domain/channel-codec";
import { Block } from "../../../domain/blocks/block";
import { Show } from "../../../domain/show";

function toChannelPlatforms(apiPlatforms) {
  return apiPlatforms.map((platform) => {
    switch (platform.ChannelPlatformType) {
      case ChannelPlatformType.Custom:
        const pushSettings = mapToPushSettings(platform);
        return new CustomChannelPlatform(
          platform.PlatformId,
          platform.IsActive,
          platform.CustomName,
          platform.CustomStreamingProtocol === 1
            ? StreamingProtocol.Hls
            : StreamingProtocol.Push,
          {
            resolution: platform.CustomHlsResolutionHeight,
            fps: platform.CustomHlsFps,
          },
          pushSettings,
          platform.AudioLanguages || [],
          toDvbSubtitles(platform.CustomDvbSubtitles),
          platform.EdgeServerIds,
        );
      default:
        return new FacebookChannelPlatform(
          platform.PlatformId,
          platform.IsActive,
        );
    }
  });
}

function mapToPushSettings(platform: any): ChannelPushSettings {
  switch (platform.CustomPushFormat) {
    case "SRT":
      return new ChannelPushSrtSettings(
        platform.CustomCodecId,
        platform.SrtPushMode,
        platform.SrtPortNum,
        platform.SrtPushAddress,
        platform.SrtPassPhrase,
        platform.SrtLatencyMs,
      );
    case "flv":
      return new ChannelPushFlvSettings(
        platform.CustomCodecId,
        platform.CustomPushUrl,
      );
    default:
      return new ChannelPushMpgtsSettings(
        platform.CustomCodecId,
        platform.CustomPushUrl,
      );
  }
}

function toDvbSubtitles(
  arr: {
    LanguageCode: string;
    FontId: number;
    FontSize: number;
    SubtitleStartPositionFromTop: number;
    SubtitleEndPositionFromTop: number;
  }[],
) {
  const dvbSubtitles = {};
  arr.forEach((x) => {
    dvbSubtitles[x.LanguageCode] = {
      fontId: x.FontId,
      size: x.FontSize,
      subtitleStartPositionFromTop: x.SubtitleStartPositionFromTop,
      subtitleEndPositionFromTop: x.SubtitleEndPositionFromTop,
    };
  });

  return dvbSubtitles;
}

@Injectable()
export class ChannelMapper {
  constructor() {}

  mapStartCreateRequest(
    name: string,
    timeZoneId: number,
    logoX: number,
    logoY: number,
    logoHeight: number,
    logoWidth: number,
    isBlock: boolean,
  ) {
    return {
      Name: name,
      IsBlock: isBlock,
      TimezoneId: timeZoneId,
      LogoX: logoX || 0,
      LogoY: logoY || 0,
      LogoHeight: logoHeight || 0,
      LogoWidth: logoWidth || 0,
    };
  }

  mapStartCreateResult(apiResult) {
    return {
      channelId: apiResult.ChannelId,
      ChannelUid: apiResult.ChannelUid,
      createdDate: moment(apiResult.CreatedDate),
      newAccessToken: apiResult.NewAccessToken,
    };
  }

  mapSaveRequest(
    channelInfo: ChannelInfo,
    initCueperiodSetting: boolean,
    availabilityZoneIdsToDeploy: number[],
  ) {
    const result: any = {
      ChannelId: channelInfo.id,
      Name: channelInfo.name,
      Description: channelInfo.description,
      PackshotUrl: channelInfo.packshotUrl,
      PlaceholderImageUrl: channelInfo.placeholderImage,
      TimezoneId: channelInfo.timezoneId,
      BurnSubtitles: channelInfo.burnSubtitles,
      SubtitleLanguage: channelInfo.subtitleLanguage,
      SubtitlesFontId: channelInfo.subtitleFontSettings.fontId,
      SubtitlesFontSize: channelInfo.subtitleFontSettings.size,
      SubtitleStartPositionFromTop:
        channelInfo.subtitleFontSettings.subtitleStartPositionFromTop,
      SubtitleEndPositionFromTop:
        channelInfo.subtitleFontSettings.subtitleEndPositionFromTop,
      IsRecordingActive: channelInfo.isRecordingActive,
      MediaIdPrefixesToAlert:
        channelInfo.uploadPlaylistCsvSettings.mediaIdPrefixesToAlert,
      UploadPlaylistHoursAhead:
        channelInfo.uploadPlaylistCsvSettings.allowedHoursFromNow,
      AllowedFileNameWildcard:
        channelInfo.uploadPlaylistCsvSettings.allowedFileNameWildcard,
      IsOneItemPerDepth: channelInfo.playOneItemPerDepth,
      WebRTCEdgeServerId: channelInfo.webRtcPreviewEdgeServerId,
      HlsEdgeServerId: channelInfo.hlsPreviewEdgeServerId,
      FuturePreviewEdgeServerId: channelInfo.futurePreviewEdgeServerId,
      IsCreatedReady: channelInfo.isCreatedReady,
      Framerate: channelInfo.framerate,
      InitCueperiodSetting: initCueperiodSetting,
      Platforms: channelInfo.platforms.map((p) => {
        const platform = {
          ChannelPlatformType: p.channelPlatformType,
          IsActive: p.isActive,
          PlatformId: p.platformId,
          EdgeServerIds: p.edgeServerIds,
        };

        if (p instanceof CustomChannelPlatform) {
          platform["CustomName"] = p.displayLabel;
          platform["CustomStreamingProtocol"] = p.streamingProtocol;
          platform["CustomHlsResolutionHeight"] = p.hlsSettings.resolution;
          platform["CustomHlsFps"] = p.hlsSettings.fps;
          platform["CustomPushFormat"] = p.pushSettings.format;
          platform["CustomCodecId"] = p.pushSettings.codecId;
          platform["AudioLanguages"] = p.audioLanguages;
          platform["CustomDvbSubtitles"] = Object.keys(p.dvbSubtitles).map(
            (x) => {
              return {
                LanguageCode: x,
                FontId: p.dvbSubtitles[x].fontId,
                FontSize: p.dvbSubtitles[x].size,
                SubtitleStartPositionFromTop:
                  p.dvbSubtitles[x].subtitleStartPositionFromTop,
                SubtitleEndPositionFromTop:
                  p.dvbSubtitles[x].subtitleEndPositionFromTop,
              };
            },
          );
          if (p.pushSettings instanceof ChannelPushMpgtsSettings) {
            platform["CustomPushUrl"] = (
              p.pushSettings as ChannelPushMpgtsSettings
            ).url;
          } else if (p.pushSettings instanceof ChannelPushSrtSettings) {
            const channelPushSrtSettings =
              p.pushSettings as ChannelPushSrtSettings;

            platform["SrtPushMode"] = channelPushSrtSettings.pushMode;
            platform["SrtPushAddress"] = channelPushSrtSettings.pushAddress;
            platform["SrtPassPhrase"] = channelPushSrtSettings.passPhrase;
            platform["SrtPortNum"] = channelPushSrtSettings.portNum;
            platform["SrtLatencyMs"] = channelPushSrtSettings.latencyMs;
          } else if (p.pushSettings instanceof ChannelPushFlvSettings) {
            platform["CustomPushUrl"] = (
              p.pushSettings as ChannelPushFlvSettings
            ).url;
          }
        }
        return platform;
      }),
      AudioNormalization: channelInfo.audioNormalization,
      AspectRatio: channelInfo.aspectRatio,
      AvailabilityZoneSettings: channelInfo.availabilityZones.map((x) => {
        return {
          AvailabilityZoneId: x.id,
          OverrideChannelJson: x.overrideChannelJson,
        };
      }),
      LogoX: channelInfo.logoX,
      LogoY: channelInfo.logoY,
      LogoHeight: channelInfo.logoHeight,
      LogoWidth: channelInfo.logoWidth,
      LogoZIndex: channelInfo.logoZIndex,
      Categories: channelInfo.templateCategories,
      IsTemplate: channelInfo.isTemplate,
      TrailerUrl: channelInfo.templateTrailerUrl,
      MainLanguageCode: channelInfo.mainLanguageCode,
      AudioLanguageFallbackTranslationSettings: (
        channelInfo.audioLangSettings || []
      ).map((s) => ({
        LanguageCode: s.languageCode,
        SkipOriginalMediaAudioLanguage: s.skipOriginalMediaAudioLanguage,
        AudioFallbackLanguages: s.audioFallbackLanguages,
      })),
      ClosedCaptions: (channelInfo.closedCaptions || []).map((c) => ({
        LanguageCode: c.languageCode,
        CaptionsTransport: c.captionsTransport,
        CaptionsType: c.captionsType,
        CaptionsServiceNumber: c.captionsServiceNumber,
      })),
      WorkingHours: channelInfo.workingHours,
      TimeSpotRules: channelInfo.timeSpotRules,
      PlaybackOffset: channelInfo.playbackOffset,
      UseLUFS: channelInfo.useLUFS,
      DeployToAvailabilitiesZoneIds: availabilityZoneIdsToDeploy,
      AutoExtendLiveShowBehaviour: channelInfo.autoExtendLiveShowBehaviour,
      IsBlockStack: channelInfo.isBlockStack,
      SchedulerDaysToLoad: channelInfo.schedulerDaysToLoad,
    };

    if (channelInfo.scteTagIds && channelInfo.scteTagIds.length > 0) {
      result.ScteOutCommandId = channelInfo.scteOutCommandId;
      result.ScteTags = [...channelInfo.scteTagIds];
    }

    return result;
  }

  mapGetChannelCodecsResult(result) {
    return result.map(
      (codec) =>
        new ChannelCodec(
          codec.Id,
          codec.Name,
          codec.IsGeneral,
          new VideoCodecSettings(
            codec.VideoCodecType,
            new Resolution(
              codec.VideoResolutionWidth,
              codec.VideoResolutionHeight,
            ),
            codec.VideoFps,
            codec.VideoProfile,
            codec.VideoBitrate,
            codec.VideoGopSize,
            codec.VideoInterlaceType,
            codec.VideoMuxOverhead,
            codec.VideoSpecialOptions,
            codec.ColourSpace,
            codec.MpegColourRange,
          ),
          new AudioCodecSettings(
            codec.AudioCodecType,
            codec.AudioChannels,
            codec.AudioSampleRate,
            codec.AudioBitrate,
            codec.AudioSpecialOptions,
          ),
        ),
    );
  }

  mapSetChannelCodecResult(codec: ChannelCodec) {
    return {
      Name: codec.name,
      VideoCodecType: codec.video.codecType,
      VideoResolutionWidth: codec.video.resolution.width,
      VideoResolutionHeight: codec.video.resolution.height,
      VideoFps: codec.video.fps,
      VideoBitrate: codec.video.bitrate,
      VideoProfile: codec.video.profile,
      VideoGopSize: codec.video.gopSize,
      VideoInterlaceType: codec.video.interlaceType,
      VideoMuxOverhead: codec.video.muxOverhead,
      AudioCodecType: codec.audio.codecType,
      AudioChannels: codec.audio.channels,
      AudioSampleRate: codec.audio.samplerate,
      AudioBitrate: codec.audio.bitrate,
      VideoSpecialOptions: codec.video.specialOptions,
      AudioSpecialOptions: codec.audio.specialOptions,
      ColourSpace: codec.video.colourSpace,
      MpegColourRange: codec.video.mpegColourRange,
    };
  }

  mapGetChannelsResult(result) {
    return result.map((channel) => {
      if (channel.IsBlock) {
        const showsCount = channel.ShowsCount;

        const packshotUrl = channel.ChannelThumbnailURL;
        let showsDuration = moment.duration(
          channel.ShowsDurationMs,
          "milliseconds",
        );

        return new Block(
          channel.ChannelId,
          channel.ChannelUid,
          channel.ChannelDescription,
          channel.ChannelName,
          packshotUrl,
          showsCount,
          showsDuration,
          channel.IsBlockStack,
        );
      } else {
        return new Channel(
          channel.ChannelId,
          channel.ChannelUid,
          channel.ChannelDescription,
          channel.ChannelName,
          channel.ChannelThumbnailURL,
          channel.IsActive,
          moment(channel.ChannelCreatedDate),
          channel.TimeZoneId,
          channel.Framerate,
          true,
          channel.IsTemplate,
          channel.PreviewUrls.map((x) => {
            return { name: x.Name, url: x.Url };
          }),
          channel.IsLiveLocked,
          channel.AvailabilityZoneIds,
          false,
          false,
        );
      }
    });
  }

  mapGetChannelInfo(result) {
    const channel = result.Channel;
    const isTemplate = result.IsTemplate;
    const availabilityZones = result.AvailabilityZones;
    const channelCategories = result.Categories || [];
    const templateTrailerUrl = result.TemplateTrailerUrl;
    const timeSpotRules = result.TimeSpotRules;
    const uploadPlaylistCsvSettings = new UploadPlaylistCsvSettings(
      result.UploadPlaylistCsvSettings.MediaIdPrefixesToAlert,
      result.UploadPlaylistCsvSettings.AllowedFileNameWildcard,
      result.UploadPlaylistCsvSettings.AllowedHoursFromNow,
    );
    const workingHours = new WorkingHoursSettings(
      channel.WorkingHours.StartDayOfWeek,
      channel.WorkingHours.EndDayOfWeek,
      channel.WorkingHours.StartTimeInMinutes,
      channel.WorkingHours.EndTimeInMinutes,
    );
    const allChannelSignals = [];
    result.ChannelSignalCommands.forEach((secEventRawItem) => {
      for (const commandName in secEventRawItem) {
        const secEventRaw = secEventRawItem[commandName];
        allChannelSignals.push(
          new ChannelSignalCommandSetting(
            secEventRaw.CommandId,
            secEventRaw.Command,
            secEventRaw.StandartType,
          ),
        );
      }
    });
    const audioLangSettings: AudioLangSettings[] = (
      result.AudioLanguageFallbackTranslationSettings || []
    ).map(
      (s) =>
        new AudioLangSettings(
          s.languageCode,
          !!s.skipOriginalMediaAudioLanguage,
          Array.isArray(s.audioFallbackLanguages)
            ? s.audioFallbackLanguages
            : [],
        ),
    );
    const closedCaptions: ClosedCaptionSetting[] = (
      channel.ClosedCaptions || []
    ).map(
      (c) =>
        new ClosedCaptionSetting(
          c.LanguageCode,
          c.CaptionsTransport,
          c.CaptionsType,
          c.CaptionsServiceNumber,
        ),
    );
    //console.log('channel-template->channelSignalSettings', channelSignalSettings);
    const channelInfo = new ChannelInfo(
      channel.ChannelId,
      channel.ChannelUid,
      channel.ChannelDescription,
      channel.ChannelName,
      channel.ChannelThumbnailURL,
      channel.IsActive,
      moment(channel.ChannelCreatedDate),
      channel.PlaceholderImageUrl,
      channel.TimezoneId,
      channel.BurnSubtitles,
      channel.SubtitlesLanguage,
      {
        fontId: channel.SubtitlesFontId,
        size: channel.SubtitlesFontSize,
        subtitleStartPositionFromTop: channel.SubtitleStartPositionFromTop,
        subtitleEndPositionFromTop: channel.SubtitleEndPositionFromTop,
      },
      channel.Framerate,
      toChannelPlatforms(channel.Platforms || []),
      channel.AspectRatio,
      availabilityZones,
      channel.PreviewUrls.map((x) => ({ name: x.Name, url: x.Url })),
      isTemplate,
      channelCategories,
      templateTrailerUrl,
      channel.ThumbnailX || 0,
      channel.ThumbnailY || 0,
      channel.ThumbnailHeight || 0,
      channel.ThumbnailWidth || 0,
      result.DepthSettings.LogoDepth,
      workingHours,
      channel.MainLanguageCode || "en",
      timeSpotRules,
      channel.IsRecordingActive,
      result.PlaySingleMediaItemPerDepth,
      channel.PlaybackOffset,
      channel.IsLiveLocked,
      result.SchedulerDaysToLoad,
      result.IsBlock,
      result.IsBlockStack,
      uploadPlaylistCsvSettings,
      result.IsCreatedReady,
      channel.AudioNormalization,
      channel.UseLUFS,
      allChannelSignals,
      channel.ScteOutCommandId,
      channel.ScteInCommandId,
      channel.ScteTags?.split("|").map((x) => parseInt(x)),
      channel.WebRtcPreviewEdgeServerId,
      channel.HlsPreviewEdgeServerId,
      channel.FuturePreviewEdgeServerId,
      result.LiveBroadcastPresetShowUid,
      result.AutoExtendLiveShowBehaviour,
      audioLangSettings,
      closedCaptions,
    );
    console.log("map-channel-info", channelInfo);
    return channelInfo;
  }

  mapChannelDistributionOption(result): ChannelDistribution {
    const channelDistribution = new ChannelDistribution();

    if (!result.Formats) {
      console.warn(
        "Channel.mapper.ts: failed to map channel distribution option. ",
        result,
      );
      return channelDistribution;
    }

    for (const format in result.Formats) {
      const formatData = result.Formats[format];
      formatData["Audio"] = formatData["Audio"].map((codec) => {
        codec["Channels"] = codec["Channels"].map((channel) => {
          return new AudioChannel(channel["DisplayName"], channel["Value"]);
        });

        return new AudioCodec(
          codec["Codec"]["DisplayName"],
          codec["Codec"]["Value"],
          codec["Bitrate"],
          codec["Channels"],
          codec["SampleRate"],
        );
      });

      formatData["Video"] = formatData["Video"].map((codec) => {
        codec["Resolutions"] = codec["Resolutions"].map((resolution) => {
          return new ResolutionPreset(
            resolution["PresetName"],
            Number.parseInt(resolution["Width"]),
            Number.parseInt(resolution["Height"]),
          );
        });

        codec["Profile"] = codec["Profile"].map((profile) => {
          return new VideoFormatProfile(profile["Name"], profile["Value"]);
        });

        codec["Progressive"] = codec["Progressive"].map((progressive) => {
          return new VideoFormatProgressive(
            progressive["Name"],
            progressive["Value"],
          );
        });

        return new VideoCodec(
          codec["Codec"]["DisplayName"],
          codec["Codec"]["Value"],
          codec["Fps"].map((fps) => fps["Value"]),
          codec["Profile"],
          codec["Progressive"],
          codec["Resolutions"],
        );
      });

      const defaultSettings = new DefaultPushCodecSettings(
        formatData["Default"]["Video"]["Name"],
        formatData["Default"]["Video"]["Value"],
        new ResolutionPreset(
          formatData["Default"]["Video"]["ResolutionPreset"]["PresetName"],
          Number.parseInt(
            formatData["Default"]["Video"]["ResolutionPreset"]["Width"],
          ),
          Number.parseInt(
            formatData["Default"]["Video"]["ResolutionPreset"]["Height"],
          ),
        ),
        formatData["Default"]["Video"]["Fps"]["Value"],
        formatData["Default"]["Video"]["Bitrate"],
        {
          name: formatData["Default"]["Video"]["Profile"]["Name"],
          id: formatData["Default"]["Video"]["Profile"]["Value"],
        },
        formatData["Default"]["Video"]["GOPSize"],
        {
          name: formatData["Default"]["Video"]["Progressive"]["Name"],
          id: formatData["Default"]["Video"]["Progressive"]["Value"],
        },
        formatData["Default"]["Video"]["MUX"],
        formatData["Default"]["Video"]["SpecialOptions"],
        formatData["Default"]["Audio"]["Name"],
        formatData["Default"]["Audio"]["Value"],
        {
          name: formatData["Default"]["Audio"]["Channels"]["DisplayName"],
          channels: formatData["Default"]["Audio"]["Channels"]["Value"],
        },
        formatData["Default"]["Audio"]["SampleRate"],
        formatData["Default"]["Audio"]["AudioBitrate"],
        formatData["Default"]["Audio"]["SpecialOptions"],
        0,
        false,
      );
      let formatSettings = new ChannelFormat(
        formatData["Name"],
        formatData["Name"],
        formatData["Url"],
        formatData["Audio"],
        formatData["Video"],
        defaultSettings,
      );
      if (formatData["SrtSettings"]) {
        const srtFormatData = formatData["SrtSettings"];
        formatSettings = new ChannelPushSrtFormat(
          formatData["Name"],
          formatData["Name"],
          formatData["Url"],
          formatData["Audio"],
          formatData["Video"],
          defaultSettings,
          srtFormatData["PushMode"],
          srtFormatData["PortNum"],
          srtFormatData["PushAddress"],
          srtFormatData["PassPhrase"],
          srtFormatData["LatencyMS"],
        );
      }
      channelDistribution.addFormat(formatSettings);
    }
    return channelDistribution;
  }
}
